/**
 * Separable box blur over RGBA pictures, the pure part of the world map's fog treatment (ADR-100 decisions 62 and 63).
 *
 * The blur is two box passes, each separable, run on colour weighted by alpha so a transparent pixel never greys
 * its neighbour, with the picture's border treated as its edge pixel repeated. All sums are integers held exactly in
 * doubles and divided once at the end, so a flat field is returned to the bit and two runs agree on every engine.
 */

const CHANNELS = 4;
const PASSES = 2;
const BUFFERS = 2;
const ALPHA = 3;
const OPAQUE = 255;

/**
 * Throws unless a value is an integer of at least one.
 *
 * @param value Value to check.
 * @param label Name used in the error message.
 * @throws RangeError when the value is not an integer of at least 1.
 */
function assertPositiveInteger(value: number, label: string): void {
  if (!Number.isInteger(value) || value < 1) {
    throw new RangeError(`${label} must be an integer of at least 1, received ${String(value)}`);
  }
}

/**
 * The length of the scratch array `blurRgba` needs for a picture of a size.
 *
 * @param width Picture width in pixels.
 * @param height Picture height in pixels.
 * @returns The number of doubles in the scratch array: two working copies of the picture.
 */
export function blurScratchSize(width: number, height: number): number {
  return width * height * CHANNELS * BUFFERS;
}

/**
 * One sliding box sum of window `2 * radius + 1` along every line of one axis, the border repeated, over all four
 * channels of a working copy.
 *
 * @param from Working copy to read.
 * @param to Working copy to write; the sums are not divided.
 * @param width Picture width in pixels.
 * @param height Picture height in pixels.
 * @param radius Radius of the window in pixels.
 * @param horizontal True to sweep along rows, false along columns.
 */
function sweep(from: Float64Array, to: Float64Array, width: number, height: number, radius: number, horizontal: boolean): void {
  const length = horizontal ? width : height;
  const lines = horizontal ? height : width;
  const step = horizontal ? CHANNELS : width * CHANNELS;
  const lineStep = horizontal ? width * CHANNELS : CHANNELS;
  const last = length - 1;
  for (let line = 0; line < lines; line++) {
    for (let channel = 0; channel < CHANNELS; channel++) {
      const base = line * lineStep + channel;
      let sum = (radius + 1) * from[base]!;
      for (let k = 1; k <= radius; k++) {
        sum += from[base + Math.min(k, last) * step]!;
      }
      for (let i = 0; i < length; i++) {
        to[base + i * step] = sum;
        sum += from[base + Math.min(i + radius + 1, last) * step]! - from[base + Math.max(i - radius, 0) * step]!;
      }
    }
  }
}

/**
 * Blurs an RGBA picture with a separable box blur of two passes, each of the given radius, so a lone pixel reaches
 * `2 * radius` pixels on each side. Colour is weighted by alpha and the border repeats its edge pixel. Nothing is
 * allocated: the caller supplies the output and the scratch, and may reuse both between calls.
 *
 * @param source Picture to read, `width * height * 4` bytes of straight (not premultiplied) RGBA; not changed.
 * @param width Picture width in pixels, an integer of at least 1.
 * @param height Picture height in pixels, an integer of at least 1.
 * @param radius Radius of each pass in pixels, an integer of at least 1.
 * @param output Array that receives the blurred picture, `width * height * 4` bytes; must not be `source`.
 * @param scratch Working doubles, at least `blurScratchSize(width, height)` long; contents on entry are ignored.
 * @throws RangeError when a size or the radius is not a positive integer, or an array is shorter or longer than its size needs.
 */
export function blurRgba(
  source: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  radius: number,
  output: Uint8ClampedArray | Uint8Array,
  scratch: Float64Array,
): void {
  assertPositiveInteger(width, "width");
  assertPositiveInteger(height, "height");
  assertPositiveInteger(radius, "radius");
  const bytes = width * height * CHANNELS;
  if (source.length !== bytes || output.length !== bytes) {
    throw new RangeError(`source and output must hold ${String(bytes)} bytes`);
  }
  if (scratch.length < blurScratchSize(width, height)) {
    throw new RangeError(`scratch must hold at least ${String(blurScratchSize(width, height))} doubles`);
  }
  const first = scratch.subarray(0, bytes);
  const second = scratch.subarray(bytes, bytes * BUFFERS);
  for (let i = 0; i < bytes; i += CHANNELS) {
    const alpha = source[i + ALPHA]!;
    first[i] = source[i]! * alpha;
    first[i + 1] = source[i + 1]! * alpha;
    first[i + 2] = source[i + 2]! * alpha;
    first[i + ALPHA] = alpha;
  }
  for (let pass = 0; pass < PASSES; pass++) {
    sweep(first, second, width, height, radius, true);
    sweep(second, first, width, height, radius, false);
  }
  const window = 2 * radius + 1;
  const weight = window ** (2 * PASSES);
  for (let i = 0; i < bytes; i += CHANNELS) {
    const alpha = first[i + ALPHA]!;
    if (alpha === 0) {
      output[i] = 0;
      output[i + 1] = 0;
      output[i + 2] = 0;
      output[i + ALPHA] = 0;
      continue;
    }
    output[i] = Math.round(first[i]! / alpha);
    output[i + 1] = Math.round(first[i + 1]! / alpha);
    output[i + 2] = Math.round(first[i + 2]! / alpha);
    output[i + ALPHA] = Math.min(OPAQUE, Math.round(alpha / weight));
  }
}
